/**
 * Regras de normalização de uma linha da GERENCIAL aplicadas NO EXTRATO
 * (rodam dentro do worker de parse, antes de a foto ir ao backend): o que é
 * serviço, o que está cancelado e qual é o término efetivo.
 *
 * Os textos chegam já normalizados por `normalizeText` (maiúsculas, sem
 * acento, espaços colapsados), a mesma régua da chave do Mapa da Produção.
 */
import { normalizeText } from "../data";
import { somarDu } from "./calendario";

export const AJUSTE_CARREGAMENTO_SEM_TERMINO = "carregamento_sem_termino";
export const AJUSTE_TERMINO_APOS_CARREGAMENTO = "termino_apos_carregamento";

const SERVICO_NO_INICIO = /^SERVICO\b/;
const SERVICO_PALAVRA = /\bSERVICO\b/;
const TROCA_PALAVRA = /\bTROCAS?\b/;

// O extrato roda no worker, que não tem o calendário da unidade: "1 dia
// útil" aqui pula só o fim de semana. Um feriado entre o carregamento e o
// término pode deixar passar um ajuste a mais (1 dia de folga), nunca
// inventar um — o término só é trocado quando passa do carregamento.
const SO_FIM_DE_SEMANA = new Set();

/**
 * Serviço não é ambiente de marcenaria (troca, peça avulsa, assistência):
 * fica fora da fila e do ritmo. Só marcador explícito conta: lote "SERV.",
 * marceneiro "SERVIÇO" ou nome que COMEÇA com SERVIÇO/TROCA. Na planilha real
 * "BANHO DE SERVIÇO", "QUARTO SERVIÇO" e "ÁREA DE SERVIÇO" são cômodos; tirar
 * esses da fila sumiria com ambientes de verdade. Os nomes duvidosos ficam
 * na fila e aparecem em `possivelServico` para o PCP conferir.
 */
export function ehServico({ ambiente, lote, marceneiro } = {}) {
  const amb = normalizeText(ambiente);
  if (SERVICO_NO_INICIO.test(amb)) return true;
  if (amb.startsWith("TROCA ")) return true;
  if (amb.includes("PECA AVULSA")) return true;
  if (normalizeText(lote).startsWith("SERV")) return true;
  return normalizeText(marceneiro) === "SERVICO";
}

/**
 * Nome que menciona serviço ou troca sem ser marcador explícito (ex.:
 * "RECEPÇÃO - SERVIÇO", "SUÍTE / TROCA DE PORTAS"): conta como ambiente, mas
 * a Qualidade lista para o PCP marcar o lote "SERV." se for serviço.
 */
export function possivelServico(linha = {}) {
  if (ehServico(linha)) return false;
  const amb = normalizeText(linha.ambiente).replace(/\bAREA DE SERVICO\b/g, "");
  return SERVICO_PALAVRA.test(amb) || TROCA_PALAVRA.test(amb);
}

export function ehCancelado({ ambiente, marceneiro } = {}) {
  if (normalizeText(marceneiro) === "CANCELADO") return true;
  return normalizeText(ambiente).includes("CANCELAD");
}

/**
 * Término efetivo da marcenaria (plano 4.1):
 * - término lançado e não depois de carregamento + 1 du → o próprio término;
 * - término depois de carregamento + 1 du → vale o carregamento (o término
 *   foi lançado atrasado: o ambiente já tinha saído);
 * - sem término e com carregamento → vale o carregamento (esqueceram de lançar).
 */
export function terminoEfetivo({ marcenaria_termino: termino, carregamento } = {}) {
  if (termino) {
    if (carregamento && termino > somarDu(carregamento, 1, SO_FIM_DE_SEMANA)) {
      return { termino: carregamento, ajuste_termino: AJUSTE_TERMINO_APOS_CARREGAMENTO };
    }
    return { termino, ajuste_termino: null };
  }
  if (carregamento) {
    return { termino: carregamento, ajuste_termino: AJUSTE_CARREGAMENTO_SEM_TERMINO };
  }
  return { termino: null, ajuste_termino: null };
}
